import { cn } from "@/lib/utils";
import React from "react";
import clamp from "lodash.clamp";
import { FiArrowLeft } from "react-icons/fi";
import InlineButton from "@/components/ui/inline-button";

interface ProgressBarProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  step: number;
  total: number;
  fullWidth?: boolean;
  handlePrevButtonClick?: () => void;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  step,
  total,
  fullWidth = false,
  handlePrevButtonClick,
  ...props
}) => {
  const cleanedTotal = Math.max(1, total);
  const clampedStep = clamp(step, 1, cleanedTotal);

  return (
    <div
      {...props}
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={cleanedTotal}
      aria-valuenow={clampedStep}
      className="flex w-full flex-row items-center gap-2 rounded-full bg-primary-100 px-4 py-2 shadow-[0px_8px_26px_0px_#66240029] md:gap-4 md:px-6 lg:gap-6 lg:px-8"
    >
      <InlineButton
        className="max-md:p-0"
        variant="reversed"
        aria-label="back button"
        onClick={handlePrevButtonClick}
      >
        <div className="flex flex-row items-center justify-center gap-1 font-montserrat text-body-3-400 md:gap-2 md:text-body-1-400">
          <FiArrowLeft />
          Back
        </div>
      </InlineButton>
      {fullWidth ? (
        <div
          className={cn(
            "h-1 flex-1 rounded-full bg-secondary-600 sm:h-[6px] lg:h-2"
          )}
        />
      ) : (
        <>
          {Array.from(Array(cleanedTotal)).map((_, n) => (
            <div
              key={`progress-${n}-of-${cleanedTotal}`}
              className={cn(
                "h-1 flex-1 rounded-full md:h-[6px] lg:h-2",
                n < clampedStep ? "bg-secondary-600" : "bg-neutral-300"
              )}
            />
          ))}
          <label className="md::ml-4 text-subheading-4-500 ml-2 font-montserrat text-secondary-500 lg:text-subheading-3-500">
            {clampedStep} of {cleanedTotal}
          </label>
        </>
      )}
    </div>
  );
};

export default ProgressBar;
